import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";

import {
  alpha,
  Box,
  Button,
  keyframes,
  Popover,
  Slide,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
  useTheme,
  Zoom,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import OTPVerificationInput from "./OTPVerificationInput";

import { StyledLink } from "@/components/AppNavigation/StyledLink";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { StoreBrandingType } from "@/types/misc";
import useStoreType from "@/utils/hooks/useStoreType";
import {
  AnalyticsTrackingEvent,
  brandclub_colors,
  CloseIcon,
  formatMoney,
  FourPointStarIcon,
  getRetailerFromId,
  UNIVERSAL_STORE_SITE,
} from "@brandclub/common-ui";
import { useSearchParams } from "react-router-dom";
import { StoreSpin } from "@/components/Loading";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";
import { useTrackActions } from "@/utils/hooks/useTracking";

const shakePhoneAnimation = keyframes`
 0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
}
`;

const ShakeSvgContainer = styled("svg")<{ shouldShake?: boolean }>(
  ({ shouldShake }) => ({
    animation: shouldShake ? `${shakePhoneAnimation} 0.1s ease-in-out` : "none",
    animationIterationCount: "4",
  })
);

const OTOPIcon = ({ shouldShake }: { shouldShake?: boolean }) => {
  return (
    <ShakeSvgContainer
      width="24.805"
      height="24.805"
      viewBox="0 0 24.805 24.805"
      shouldShake={shouldShake}
    >
      <g data-name="Group 22914">
        <g data-name="Group 22913">
          <g data-name="notification (2)">
            <path
              data-name="Path 26093"
              d="M12.884 13.981H7.115a.865.865 0 1 1 0-1.731h5.769a.865.865 0 1 1 0 1.731z"
              transform="translate(-.481 .441)"
              style={{ fill: "#fff" }}
            />
            <path
              data-name="Path 26094"
              d="M17.5 17.981H7.115a.865.865 0 1 1 0-1.731H17.5a.865.865 0 1 1 0 1.731z"
              transform="translate(-.481 1.056)"
              style={{ fill: "#fff" }}
            />
            <path
              data-name="Path 26095"
              d="M17.113 26.055h-6.922c-6.265 0-8.941-2.677-8.941-8.941v-6.923c0-6.265 2.677-8.941 8.941-8.941h5.769a.865.865 0 0 1 0 1.731h-5.769c-5.319 0-7.211 1.892-7.211 7.211v6.922c0 5.319 1.892 7.211 7.211 7.211h6.922c5.319 0 7.211-1.892 7.211-7.211v-5.769a.865.865 0 1 1 1.731 0v5.769c0 6.264-2.677 8.941-8.942 8.941z"
              style={{ fill: "#fff" }}
              transform="translate(-1.25 -1.25)"
            />
            <g
              data-name="Ellipse 1513"
              transform="translate(16.979 .826)"
              style={{ stroke: "#fff", fill: "#fff" }}
            >
              <circle cx="3.5" cy="3.5" r="3.5" style={{ stroke: "none" }} />
              <circle cx="3.5" cy="3.5" r="3" style={{ fill: "none" }} />
            </g>
          </g>
        </g>
      </g>
    </ShakeSvgContainer>
  );
};

const QuestionMarkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
    >
      <g data-name="Group 22835">
        <path
          data-name="Path 26085"
          d="M26.95 27.766h-.008a.877.877 0 0 1-.869-.885v-.438a.724.724 0 0 1 0-.075 4.2 4.2 0 0 1 1.649-2.686c.231-.213.449-.414.635-.624a1.188 1.188 0 0 0 .21-1.411 1.937 1.937 0 0 0-2.128-.763 1.631 1.631 0 0 0-1.185 1.241.877.877 0 0 1-1.695-.454 3.326 3.326 0 0 1 2.487-2.5 3.649 3.649 0 0 1 4.067 1.629 2.928 2.928 0 0 1-.436 3.416 10.49 10.49 0 0 1-.758.749c-.629.581-1.023.969-1.089 1.53v.4a.876.876 0 0 1-.876.87z"
          transform="translate(-17.007 -14.785)"
          style={{ fill: "#dcdddc" }}
        />
      </g>
      <g data-name="Group 22836">
        <path
          data-name="Path 26086"
          d="M30.2 42.674a.884.884 0 0 1-.618-.254.873.873 0 0 1 0-1.241.907.907 0 0 1 1.237 0 .87.87 0 0 1 .259.618.868.868 0 0 1-.877.877z"
          transform="translate(-20.257 -26.958)"
          style={{ fill: "#dcdddc" }}
        />
      </g>
      <g data-name="Group 22837">
        <path
          data-name="Path 26087"
          d="M15.971 26.036a9.915 9.915 0 0 1-2.579-.339 10.057 10.057 0 1 1 2.579.339zm.048-18.46a8.463 8.463 0 0 0-2.226 16.634 8.391 8.391 0 0 0 2.178.286 8.468 8.468 0 0 0 3.808-16.042 8.439 8.439 0 0 0-3.759-.878z"
          transform="translate(-5.999 -6.036)"
          style={{ fill: "#dcdddc" }}
        />
      </g>
    </svg>
  );
};

const FormContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  maxWidth: "656px",
  width: "100%",
  ".title": {
    paddingBottom: "0px",
    fontSize: "24px",
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    marginBottom: "24px",
    [theme.breakpoints.down("md")]: {
      fontSize: "20px",
      marginBottom: "12px",
    },
  },
  ".subtitle": {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "12px",
    fontSize: "20px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      marginBottom: "8px",
    },
  },
  ".description": {
    fontSize: "16px",
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
    textAlign: "left",
    marginBottom: "16px",
    [theme.breakpoints.down("md")]: {
      fontSize: "14px",
      marginBottom: "14px",
    },
  },
}));

const SecurityIcon = () => {
  const theme = useTheme();

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8.541"
      height="10"
      viewBox="0 0 8.541 10"
      fill={theme.vars.palette.text.primary}
    >
      <path
        data-name="Path 26084"
        d="M45.894 4.155a17.355 17.355 0 0 0-.225-2.5.78.78 0 0 0-.408-.367C44.09.837 42.937.4 41.814.033a.555.555 0 0 0-.351 0c-1.123.369-2.275.8-3.447 1.256a.781.781 0 0 0-.376.312 10.633 10.633 0 0 0-.283 2.479 7.16 7.16 0 0 0 .18 2.218 3.54 3.54 0 0 0 .639 1.144 8.264 8.264 0 0 0 1.374 1.321 11.512 11.512 0 0 0 1.84 1.183.561.561 0 0 0 .5 0 11.543 11.543 0 0 0 1.84-1.185 8.292 8.292 0 0 0 1.37-1.32 3.443 3.443 0 0 0 .662-1.223 9.683 9.683 0 0 0 .13-2.063zM42.1 5.274v.91a.464.464 0 1 1-.928 0v-.9a1.016 1.016 0 1 1 .928-.013z"
        transform="translate(-37.355 -.005)"
        style={{ opacity: 0.3 }}
      />
    </svg>
  );
};

const Security = () => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      gap="4px"
      alignItems="center"
      flexDirection="row"
      lineHeight="18px"
      fontWeight="500"
      marginTop="8px"
      sx={{
        fontSize: "12px",
        color: alpha(theme.palette.text.primary, 0.3),
        [theme.breakpoints.down("md")]: {
          fontSize: "12px",
        },
      }}
    >
      <SecurityIcon />
      Secure and encrypted
    </Box>
  );
};

const OTPContainer = styled("div")(({ theme }) => ({
  padding: "20px 0px",
  width: "100%",
  maxWidth: "340px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "100%",
  },

  "& > div": {
    paddingLeft: "20px",
    paddingRight: "20px",
    boxSizing: "border-box",
  },
  ".header": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "0px",
    ".logo": {
      height: "12.2px",
    },
    ".close": {
      backgroundColor: brandclub_colors.blueWhite,
      width: "24px",
      height: "24px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
    },
  },
  ".title": {
    marginTop: "20px",
    fontSize: "16px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    marginBottom: "0px",
  },
  ".description": {
    marginTop: "8px",
    marginBottom: "0px",
    fontSize: "14px",
    textAlign: "center",
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
  },
  ".otp-input-container": {
    width: "100%",
    maxWidth: "340px",
    marginTop: "20px",
    marginBottom: "0px",
  },
  ".resend-code-container": {
    fontSize: "12px",
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    marginTop: "8px",
    marginBottom: "0px",
  },
  ".reward_available": {
    fontSize: "12px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    backgroundColor: alpha(theme.palette.secondary.main, 0.4),
    textAlign: "center",
    marginTop: "24px",
    marginBottom: "0px",
    height: "44px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },
  ".terms": {
    marginTop: "20px",
    marginBottom: "0px",
    width: "100%",
    fontSize: "10px",
    fontWeight: 500,
    color: alpha(theme.palette.text.primary, 0.3),
    textAlign: "center",
    maxWidth: "240px",
  },
}));

const EnterOTP = ({
  phoneNumber,
  changePhoneNumber,
  otpSession,
  clearOtpSession,
  setOtpSession,
  onClose,
}: {
  phoneNumber: string;
  changePhoneNumber: () => void;
  otpSession: { username: string } | undefined;
  clearOtpSession: () => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
  onClose: () => void;
}) => {
  const [trackAction] = useTrackActions();
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [isSendingOTP, setIsSendingOTP] = useState<boolean>(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);
  const hasOtpSession = !!otpSession?.username;
  const lastSubmittedCodeRef = useRef<string>("");
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );

  const { rewardAmounts } = useCheckoutCartInfo();

  const rewardAmount = rewardAmounts.totalRewardAmount;

  const dispatch = useAppDispatch();
  const {
    submittingSignIn,
    otpError,
    resendCoolDown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  useEffect(() => {
    const OTP_EXPECTED_LENGTH = 6;
    if (
      verificationCode.length === OTP_EXPECTED_LENGTH &&
      verificationCode !== lastSubmittedCodeRef.current
    ) {
      lastSubmittedCodeRef.current = verificationCode;
      setIsVerifyingOTP(true);
      clearOtpError();
      verifyOtp(
        verificationCode,
        otpSession,
        () => {
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_SIGN_IN_SUCCESS, {
            phoneNumber: phoneNumber,
          });
          dispatch(reloadProfile());
          setIsVerifyingOTP(false);
        },
        () => {
          setIsVerifyingOTP(false);
        }
      );
      trackAction?.(AnalyticsTrackingEvent.CHECKOUT_SIGN_IN_OTP_SUBMIT, {
        phoneNumber: phoneNumber,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOtp, verificationCode, otpSession, dispatch, clearOtpError]);

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      clearOtpSession();
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber, clearOtpSession]);

  const formattedPhoneNumber = formatPhone(phoneNumber, "national");

  const resendOTP = () => {
    setIsSendingOTP(true);
    resendOtp(
      phoneNumber,
      false,
      (session) => {
        setOtpSession(session);
        setIsSendingOTP(false);
      },
      () => {
        setIsSendingOTP(false);
      }
    );
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_SIGN_IN_OTP_RESEND, {
      phoneNumber: phoneNumber,
    });
  };

  const theme = useTheme();
  const linkPrefix =
    domainConfig?.storeBrandingType === "CustomDTCStore"
      ? UNIVERSAL_STORE_SITE
      : "";

  return (
    <OTPContainer>
      <div className="header">
        <img
          src={
            "https://media.brandclub.com/brandclub/image_asset/brandclub_logo_no_star.svg"
          }
          alt="logo"
          className="logo"
        />
        <div className="close" onClick={onClose} role="button">
          <CloseIcon
            sx={(theme) => ({
              width: 8,
              height: 8,
              color: theme.vars.palette.text.primary,
            })}
          />
        </div>
      </div>
      <div className="title">Confirm it's you</div>
      <div className="description">
        Enter the code sent to {formattedPhoneNumber} to securely use your saved
        information.
      </div>

      <div className="otp-input-container">
        <OTPVerificationInput
          value={verificationCode}
          onChange={(value) => {
            setVerificationCode(value);
            clearOtpError();
          }}
          maxLength={6}
          error={!!otpError}
          disabled={submittingSignIn || isSendingOTP || isVerifyingOTP}
        />
      </div>
      <div className="resend-code-container">
        <Button
          type="submit"
          onClick={() => {
            if (resendCoolDown > 0 || submittingSignIn) {
              return;
            }
            setVerificationCode("");
            clearOtpError();
            resendOTP();
          }}
          variant="text"
          disableRipple
          sx={{
            textTransform: "none",
            color: theme.vars.palette.text.primary,
            fontSize: "14px",
            fontWeight: 700,
            padding: "0",
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {resendCoolDown > 0
            ? `Resend code in 0:${resendCoolDown}`
            : "Send a new code"}
        </Button>
      </div>
      {rewardAmount ? (
        <div className="reward_available">
          <FourPointStarIcon
            sx={{
              width: "14px",
              height: "14px",
              color: theme.vars.palette.text.primary,
            }}
          />
          Earn {formatMoney(rewardAmount)} in cash rewards on this purchase
        </div>
      ) : null}
      <div className="terms">
        By continuing, you agree to Brandclub's{" "}
        <StyledLink
          to={linkPrefix + "/terms"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: alpha(theme.palette.text.primary, 0.3),
          }}
        >
          Terms
        </StyledLink>
        ,{" "}
        <StyledLink
          to={linkPrefix + "/privacy"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: alpha(theme.palette.text.primary, 0.3),
          }}
        >
          Privacy Policy
        </StyledLink>
        , and{" "}
        <StyledLink
          to={linkPrefix + "/smsterms"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: alpha(theme.palette.text.primary, 0.3),
          }}
        >
          SMS Terms
        </StyledLink>
        .
      </div>
    </OTPContainer>
  );
};

const OTPPopover = ({
  phoneNumber,
  setPhoneNumber,
  setOtpSession,
  otpSession,
  onClose,
}: {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
  otpSession: { username: string } | undefined;
  onClose: () => void;
}) => {
  return (
    <EnterOTP
      onClose={onClose}
      phoneNumber={phoneNumber}
      changePhoneNumber={() => {
        setPhoneNumber("");
        setOtpSession(undefined);
      }}
      otpSession={otpSession}
      setOtpSession={setOtpSession}
      clearOtpSession={() => setOtpSession(undefined)}
    />
  );
};

const OtpHandleButtonContainer = styled("button")(({ theme }) => ({
  outline: "none",
  border: "none",
  backgroundColor: theme.palette.primary.main,
  width: 44,
  height: 44,

  borderRadius: 6,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

const StyledTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      {...props}
      arrow
      placement="top"
      slots={{
        transition: Zoom,
      }}
      slotProps={{
        transition: {
          timeout: 200,
        },

        popper: {
          sx: {
            [`& .${tooltipClasses.arrow}`]: {
              color: (theme) => theme.palette.primary.main,
            },
            [`& .${tooltipClasses.tooltip}`]: {
              margin: "20px",
              backgroundColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText,
              fontFamily: "inherit",
              fontSize: 10,
              textAlign: "center",
              fontWeight: 500,
              maxWidth: 110,
              width: "fit-content",
              boxSizing: "border-box",
              padding: "7px 10px",
              marginBottom: "7px !important",
            },
          },
        },
      }}
    />
  );
};

const UserPhoneInputAdornment = ({
  submittingSignIn,
  hasOtp,
  onOtpClick,
  shouldShake,
}: {
  submittingSignIn: boolean;
  hasOtp: boolean;
  onOtpClick: () => void;
  shouldShake?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  if (submittingSignIn) {
    return (
      <OtpHandleButtonContainer
        sx={{
          background: "none",
          cursor: "default",
          height: "fit-content",
        }}
      >
        <StoreSpin size={20} spinColor={brandclub_colors.lightGray} />
      </OtpHandleButtonContainer>
    );
  } else if (hasOtp) {
    return (
      <OtpHandleButtonContainer onClick={onOtpClick}>
        <OTOPIcon shouldShake={shouldShake} />
      </OtpHandleButtonContainer>
    );
  }

  console.log("open", open);
  return (
    <StyledTooltip
      sx={{
        margin: 20,
      }}
      open={open}
      title="In case we need to contact you about your order"
    >
      <OtpHandleButtonContainer
        onMouseEnter={() => {
          setOpen(true);
        }}
        onMouseLeave={() => {
          setOpen(false);
        }}
        onTouchStart={() => {
          setOpen(true);
        }}
        sx={{
          background: "none",
          cursor: "default",
          height: "fit-content",
        }}
      >
        <QuestionMarkIcon />
      </OtpHandleButtonContainer>
    </StyledTooltip>
  );
};

const CheckoutSignIn = () => {
  const storeType = useStoreType();
  const [searchParams] = useSearchParams();
  const checkoutRetailerId = searchParams.get("retailerId");
  const { items } = useCheckoutCartInfo();
  const [trackAction] = useTrackActions();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const orderRetailerId = items?.[0]?.retailerId;
  const [submited, setSubmited] = useState<boolean>(false);
  const brandAppName = useAppSelector(
    ({ appConfig }) => appConfig?.brandAppConfig?.appName
  );
  const [firstTimeSignIn, setFirstTimeSignIn] = useState(true);
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>(undefined);

  const [openOTP, setOpenOTP] = useState<boolean>(false);
  const [shouldShake, setShouldShake] = useState<boolean>(false);

  const hasOtpSession = !!otpSession;

  const hasValidPhoneNumber = validatePhone(phoneNumber);

  const ref = useRef<HTMLDivElement>(null);

  const handlePopoverOpen = () => {
    setOpenOTP(true);
    // Force recalculation after a brief delay
    setTimeout(() => {
      if (ref.current) {
        // This will trigger a position recalculation
        setOpenOTP(false);
        setOpenOTP(true);
      }
    }, 10);
  };

  const handlePopoverClose = () => {
    setOpenOTP(false);
    // Trigger shake animation when popover closes
    setShouldShake(true);
    // Reset the shake state after animation completes
    setTimeout(() => {
      setShouldShake(false);
    }, 400); // 0.1s * 4 iterations = 400ms
  };

  const onSignIn = () => {
    clearOtpError();
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
      handlePopoverOpen();
    });
    trackAction(AnalyticsTrackingEvent.CHECKOUT_SIGN_IN_PHONE_SUBMIT, {
      phoneNumber: phoneNumberInput,
    });
  };

  useEffect(() => {
    if (validatePhone(phoneNumber) && firstTimeSignIn) {
      onSignIn();
      setFirstTimeSignIn(false);
      setSubmited(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, firstTimeSignIn]);

  const trySubmitPhoneNumber = (phoneNumber: string) => {
    if (validatePhone(phoneNumber)) {
      onSignIn();
      setFirstTimeSignIn(false);
      setSubmited(true);
    }
  };

  const { signIn, submittingSignIn, clearOtpError } =
    useContext(UserLoginContext);

  const retailerId = orderRetailerId || +(checkoutRetailerId ?? 0);

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      flexDirection="column"
      alignItems={"center"}
    >
      <FormContainer>
        <div className="title">Secure checkout</div>
        <div className="subtitle">
          <div className="subtitle_head">Contact</div>
          <div className="subtitle_step">1 of 4</div>
        </div>
        <div className="description">
          {`${isDtc ? `${brandAppName} uses Brandclub to` : "Brandclub"}`}{" "}
          securely {isDtc ? "process" : "processes"} your order with{" "}
          {retailerId ? `${getRetailerFromId(retailerId)}` : "Retailer"}, so you
          can enjoy fast, free shipping and earn cash back rewards.
        </div>

        <CheckoutInput
          disabled={submittingSignIn}
          ref={ref}
          label="Phone Number"
          name="phoneNumber"
          autoComplete="tel"
          value={phoneNumber}
          size="medium"
          style={{
            minWidth: "300px",
            maxWidth: "100%",
            backgroundColor: "#fff",
          }}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value?.trim());
            setSubmited(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              trySubmitPhoneNumber(phoneNumber);
            }
          }}
          InputLabelProps={{
            sx: {
              top: "5px",
              "&.MuiInputLabel-shrink": {
                top: "2px",
              },
            },
          }}
          slotProps={{
            input: {
              inputComponent: NumberFormatCustom as any,
              inputProps: {
                format: getPhoneNumberMasking(phoneNumber || ""),
              },
              sx: (theme) => ({
                paddingRight: "8px",
                height: "61px",
                ".MuiInputBase-input": {
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: theme.palette.primary.main,
                    fontSize: "14px",
                  },
                },
                [theme.breakpoints.down("md")]: {
                  height: "61px",
                },
              }),

              disableUnderline: true,
              endAdornment: (
                <UserPhoneInputAdornment
                  submittingSignIn={submittingSignIn}
                  hasOtp={!!hasOtpSession}
                  onOtpClick={() => {
                    if (hasOtpSession && submited) {
                      handlePopoverOpen();
                    } else if (!submited && hasValidPhoneNumber) {
                      trySubmitPhoneNumber(phoneNumber);
                    }
                  }}
                  shouldShake={shouldShake}
                />
              ),
            },
          }}
          sx={{
            flex: 1,
          }}
        />
        <Popover
          container={() => document.body}
          id="mouse-over-popover"
          open={openOTP}
          anchorEl={isMobile ? null : ref.current}
          disablePortal={!isMobile}
          onClose={handlePopoverClose}
          disableRestoreFocus
          anchorOrigin={{
            vertical: isMobile ? "bottom" : "center",
            horizontal: isMobile ? "center" : "right",
          }}
          transformOrigin={{
            vertical: isMobile ? "bottom" : "center",
            horizontal: isMobile ? "center" : "left",
          }}
          anchorPosition={
            isMobile
              ? {
                  top: window.innerHeight - 10,
                  left: 0,
                }
              : undefined
          }
          slots={{
            transition: isMobile ? Slide : Zoom,
          }}
          slotProps={{
            paper: {
              sx: {
                width: isMobile ? "100vw" : "auto",
                maxWidth: isMobile ? "100vw" : "auto",
                ...(isMobile && {
                  position: "fixed !important",
                  bottom: "0 !important",
                  left: "0 !important",
                  right: "0 !important",
                  top: "auto !important",
                  borderRadius: "16px 16px 0 0",
                  maxHeight: "90vh",
                  margin: "0 !important",
                  transform: "none !important",
                  zIndex: 1300,
                }),
                overflow: "visible",
                "&:before": {
                  content: '""',
                  display: isMobile ? "none" : "block",
                  width: "0",
                  height: "0",
                  position: "absolute",
                  borderTop: "15px solid transparent",
                  borderBottom: "15px solid transparent",
                  borderRight: "15px solid #fff",
                  left: "-14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                },
              },
            },
            transition: {
              timeout: 300,
              ...(isMobile && {
                direction: "up" as const,
              }),
            },
          }}
        >
          <OTPPopover
            onClose={() => {
              handlePopoverClose();
            }}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            setOtpSession={setOtpSession}
            otpSession={otpSession}
          />
        </Popover>
        <Security />
      </FormContainer>
    </Box>
  );
};

export default CheckoutSignIn;
